import { useState } from "react";
import { CheckCircleIcon, XCircleIcon, ChevronRightIcon } from "lucide-react";
import { AcademicianStartupButton } from "@/components/academician/academicianstartup-button";
import { Question } from "@/components/academician/question-editor";
import { Progress } from "@/components/ui/progress";

function normalizeAnswer(text: string): string {
  return text.toLowerCase().trim().replace(/\s+/g, " ").replace(/[^a-z0-9\s]/g, "");
}

interface QuizPreviewProps {
  title: string;
  description?: string;
  questions: Question[];
  passingScore: number;
  onClose?: () => void;
  className?: string;
}

export function QuizPreview({
  title,
  description,
  questions,
  passingScore,
  onClose,
  className = "",
}: QuizPreviewProps) {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [fitbAnswers, setFitbAnswers] = useState<Record<string, string>>({});
  const [showResults, setShowResults] = useState(false);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [hasAnswered, setHasAnswered] = useState(false);

  if (!questions || questions.length === 0) {
    return (
      <div className={`bg-background ${className}`}>
        <div className="max-w-3xl mx-auto p-8">
          <div className="text-center space-y-4">
            <h2 className="text-2xl font-bold text-foreground">No Questions Available</h2>
            <p className="text-muted-foreground">Please add questions to preview the quiz.</p>
            <AcademicianStartupButton variant="primary" onClick={onClose}>
              Close Preview
            </AcademicianStartupButton>
          </div>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];

  if (!currentQuestion) {
    return (
      <div className={`bg-background ${className}`}>
        <div className="max-w-3xl mx-auto p-8">
          <div className="text-center space-y-4">
            <h2 className="text-2xl font-bold text-foreground">Invalid Question</h2>
            <p className="text-muted-foreground">The current question is missing or invalid.</p>
            <AcademicianStartupButton variant="primary" onClick={onClose}>
              Close Preview
            </AcademicianStartupButton>
          </div>
        </div>
      </div>
    );
  }

  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;
  const optionLabels = currentQuestion.type === "true-false" ? ["T", "F"] : ["A", "B", "C", "D"];

  const handleSelectOption = (optionId: string) => {
    if (!hasAnswered) {
      setSelectedOption(optionId);
    }
  };

  const handleSubmitAnswer = () => {
    if (currentQuestion.type === "fill-in-the-blank") {
      const typed = fitbAnswers[currentQuestion.id] || "";
      if (!typed.trim()) return;
      setAnswers({ ...answers, [currentQuestion.id]: typed });
      setHasAnswered(true);
    } else {
      if (selectedOption) {
        setAnswers({ ...answers, [currentQuestion.id]: selectedOption });
        setHasAnswered(true);
      }
    }
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedOption(null);
      setHasAnswered(false);
    } else {
      setShowResults(true);
    }
  };

  const calculateScore = () => {
    let correct = 0;
    questions.forEach((question) => {
      if (!question) return;
      const answer = answers[question.id];
      if (!answer) return;

      if (question.type === "fill-in-the-blank") {
        const normalized = normalizeAnswer(answer);
        const accepted = (question.correctAnswers || []).map(a => normalizeAnswer(a));
        if (accepted.includes(normalized)) correct++;
      } else {
        if (!question.options || !Array.isArray(question.options)) return;
        const correctOption = question.options.find((opt) => opt && opt.isCorrect);
        if (correctOption && answer === correctOption.id) correct++;
      }
    });
    return questions.length > 0 ? Math.round((correct / questions.length) * 100) : 0;
  };

  const isFitbCorrect = (question: Question, answer: string) => {
    const normalized = normalizeAnswer(answer);
    return (question.correctAnswers || []).some(a => normalizeAnswer(a) === normalized);
  };

  if (showResults) {
    const score = calculateScore();
    const passed = score >= passingScore;

    return (
      <div className={`bg-background ${className}`}>
        <div className="max-w-3xl mx-auto p-8">
          <div className="text-center space-y-6">
            <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full ${
              passed ? "bg-green-500/10" : "bg-destructive/10"
            }`}>
              {passed ? (
                <CheckCircleIcon className="h-10 w-10 text-green-500" />
              ) : (
                <XCircleIcon className="h-10 w-10 text-destructive" />
              )}
            </div>

            <div>
              <h2 className="text-3xl font-bold text-foreground mb-2">
                {passed ? "Congratulations!" : "Keep Learning"}
              </h2>
              <p className="text-lg text-muted-foreground">
                {passed
                  ? "You've passed the quiz!"
                  : "You didn't pass this time, but you can try again."}
              </p>
            </div>

            <div className="bg-card border border-border rounded-lg p-6 inline-block">
              <div className="text-5xl font-bold text-foreground mb-2">{score}%</div>
              <div className="text-sm text-muted-foreground">Passing score: {passingScore}%</div>
            </div>

            <div className="flex gap-3 justify-center">
              <AcademicianStartupButton variant="secondary" onClick={onClose}>
                Close Preview
              </AcademicianStartupButton>
              <AcademicianStartupButton
                variant="primary"
                onClick={() => {
                  setCurrentQuestionIndex(0);
                  setAnswers({});
                  setFitbAnswers({});
                  setShowResults(false);
                  setSelectedOption(null);
                  setHasAnswered(false);
                }}
              >
                Retake Quiz
              </AcademicianStartupButton>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-background ${className}`}>
      <div className="max-w-3xl mx-auto p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">{title}</h1>
          {description && <p className="text-muted-foreground">{description}</p>}
        </div>

        {/* Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">
              Question {currentQuestionIndex + 1} of {questions.length}
            </span>
            <span className="text-sm font-medium text-foreground">{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Question */}
        <div className="bg-card border border-border rounded-lg p-8 mb-6">
          {/* Question Image */}
          {currentQuestion.imageUrl && (
            <div className="mb-4">
              <img src={currentQuestion.imageUrl} alt="Question" className="max-h-60 rounded-md border border-border" />
            </div>
          )}

          <h2 className="text-xl font-semibold text-foreground mb-6">{currentQuestion.text}</h2>

          {/* Options for MC / TF */}
          {currentQuestion.type !== "fill-in-the-blank" && currentQuestion.options && (
            <div className="space-y-3">
              {currentQuestion.options.map((option, index) => {
                const isSelected = selectedOption === option.id;
                const isCorrect = option.isCorrect;
                const showCorrectness = hasAnswered;

                let optionClasses =
                  "w-full p-4 rounded-lg border-2 text-left transition-all flex items-start gap-3";

                if (!hasAnswered) {
                  optionClasses += isSelected
                    ? " border-primary bg-primary/5"
                    : " border-border bg-background hover:border-primary/50 hover:bg-muted";
                } else {
                  if (isCorrect) {
                    optionClasses += " border-green-500 bg-green-500/5";
                  } else if (isSelected && !isCorrect) {
                    optionClasses += " border-destructive bg-destructive/5";
                  } else {
                    optionClasses += " border-border bg-muted opacity-60";
                  }
                }

                return (
                  <div key={option.id}>
                    <button
                      onClick={() => handleSelectOption(option.id)}
                      disabled={hasAnswered}
                      className={optionClasses}
                    >
                      <div className="flex items-center justify-center w-8 h-8 rounded-md bg-muted text-foreground font-medium text-sm shrink-0">
                        {optionLabels[index]}
                      </div>
                      <div className="flex-1">
                        <div className="text-foreground">{option.text}</div>
                        {showCorrectness && isCorrect && (
                          <div className="flex items-center gap-2 mt-2 text-sm text-green-600 dark:text-green-400">
                            <CheckCircleIcon className="h-4 w-4" />
                            <span>Correct answer</span>
                          </div>
                        )}
                        {showCorrectness && isSelected && !isCorrect && (
                          <div className="flex items-center gap-2 mt-2 text-sm text-destructive">
                            <XCircleIcon className="h-4 w-4" />
                            <span>Incorrect</span>
                          </div>
                        )}
                      </div>
                    </button>

                    {hasAnswered && (isCorrect || isSelected) && option.explanation && (
                      <div className="ml-11 mt-2 p-3 bg-muted rounded-md">
                        <p className="text-sm text-muted-foreground">{option.explanation}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Fill in the Blank input */}
          {currentQuestion.type === "fill-in-the-blank" && (
            <div className="space-y-3">
              <input
                type="text"
                value={fitbAnswers[currentQuestion.id] || ""}
                onChange={(e) => setFitbAnswers({ ...fitbAnswers, [currentQuestion.id]: e.target.value })}
                disabled={hasAnswered}
                placeholder="Type your answer here..."
                className="w-full p-4 rounded-lg border-2 border-border bg-background text-foreground text-base focus:border-primary focus:outline-none disabled:opacity-70"
              />
              {hasAnswered && (
                <div className={`p-3 rounded-md ${
                  isFitbCorrect(currentQuestion, answers[currentQuestion.id] || "")
                    ? "bg-green-500/5 border border-green-500"
                    : "bg-destructive/5 border border-destructive"
                }`}>
                  {isFitbCorrect(currentQuestion, answers[currentQuestion.id] || "") ? (
                    <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                      <CheckCircleIcon className="h-4 w-4" />
                      <span>Correct!</span>
                    </div>
                  ) : (
                    <div className="text-sm">
                      <div className="flex items-center gap-2 text-destructive mb-1">
                        <XCircleIcon className="h-4 w-4" />
                        <span>Incorrect</span>
                      </div>
                      <div className="text-muted-foreground">
                        Correct answer: <span className="font-medium text-foreground">{(currentQuestion.correctAnswers || [])[0]}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-between items-center">
          <AcademicianStartupButton variant="ghost" onClick={onClose}>
            Close Preview
          </AcademicianStartupButton>

          {!hasAnswered ? (
            <AcademicianStartupButton
              variant="primary"
              onClick={handleSubmitAnswer}
              disabled={
                currentQuestion.type === "fill-in-the-blank"
                  ? !(fitbAnswers[currentQuestion.id] || "").trim()
                  : !selectedOption
              }
            >
              Submit Answer
            </AcademicianStartupButton>
          ) : (
            <AcademicianStartupButton
              variant="primary"
              onClick={handleNext}
              rightIcon={<ChevronRightIcon />}
            >
              {currentQuestionIndex < questions.length - 1 ? "Next Question" : "View Results"}
            </AcademicianStartupButton>
          )}
        </div>
      </div>
    </div>
  );
}
